import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../utils/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import LoadingComponent from "../../../components/LoadingComponent";
import NoData from "../../../components/NoData";
import ColumnSearch from "../../../components/ColumnSearch";
import Button from "../../../components/Button";
import { XMarkIcon } from "@heroicons/react/20/solid";
import EditableCell from "../../../components/EditableCell";
import EditableDateCell from "../../../components/EditableDateCell";
import dayjs from "dayjs";

export default function InactiveRentals() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const { data: inactiveRentalsData, isLoading } = useQuery({
    queryKey: ["inactive-rentals", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/inactive-counters?${params.toString()}`
      );
      return response.data.data.data;
    },
    keepPreviousData: true,
  });

  const editCellMutation = useMutation({
    mutationFn: ({ id, field, value }) =>
      axiosInstance.put(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}`,
        { [field]: value }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["inactive-rentals"]);
      toast.success("Rental updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update rental. Please try again.");
      console.error("Update rental error:", error);
    },
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page");
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["inactive-rentals"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["inactive-rentals"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["inactive-rentals"]);
  };

  const rentals = Array.isArray(inactiveRentalsData) ? inactiveRentalsData : [];

  return (
    <div className="mt-8 flow-root bg-white rounded-lg">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-gray-50 rounded-lg mb-4">
            <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
              Inactive Rentals
            </h4>
            <Button
              variant="secondary"
              size="sm"
              icon={<XMarkIcon className="h-4 w-4" />}
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-black">
                <tr>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Client"
                      columnKey="client_id"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Client..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Serial Number"
                      columnKey="serial_number"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Serial..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Status"
                      columnKey="status"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Status..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="B&W Counter"
                      columnKey="black_white_counter"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Last Billed B&W"
                      columnKey="last_billed_black_white_counter"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Color Counter"
                      columnKey="color_counter"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Last Billed Color"
                      columnKey="last_billed_color_counter"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Phone"
                      columnKey="phone"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Phone..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Email"
                      columnKey="email"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Email..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Actual End Date"
                      columnKey="actual_end_date"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      isDate={true}
                      placeholder="Search by Date..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Last Counter Date"
                      columnKey="last_date_of_counter_activation"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      isDate={true}
                      placeholder="Search by Date..."
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={11} className="text-center py-10">
                      <LoadingComponent />
                    </td>
                  </tr>
                ) : rentals.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-10">
                      <NoData message="No inactive rentals found" />
                    </td>
                  </tr>
                ) : (
                  rentals.map((rental, index) => (
                    <tr
                      key={rental.id}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {rental.client?.unique_id || "---"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.serial_number || ""}
                          displayValue={rental.serial_number || "---"}
                          id={rental.id}
                          field="serial_number"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "serial_number",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.status || ""}
                          displayValue={
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              {rental.status || "---"}
                            </span>
                          }
                          id={rental.id}
                          field="status"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "status",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.black_white_counter || ""}
                          displayValue={rental.black_white_counter ?? "---"}
                          id={rental.id}
                          field="black_white_counter"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "black_white_counter",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.last_billed_black_white_counter || ""}
                          displayValue={rental.last_billed_black_white_counter ?? "---"}
                          id={rental.id}
                          field="last_billed_black_white_counter"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "last_billed_black_white_counter",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.color_counter || ""}
                          displayValue={rental.color_counter ?? "---"}
                          id={rental.id}
                          field="color_counter"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "color_counter",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.last_billed_color_counter || ""}
                          displayValue={rental.last_billed_color_counter ?? "---"}
                          id={rental.id}
                          field="last_billed_color_counter"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "last_billed_color_counter",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.phone || ""}
                          displayValue={rental.phone || "---"}
                          id={rental.id}
                          field="phone"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "phone",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={rental.email || ""}
                          displayValue={rental.email || "---"}
                          id={rental.id}
                          field="email"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "email",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableDateCell
                          value={rental.actual_end_date || ""}
                          displayValue={
                            rental.actual_end_date
                              ? dayjs(rental.actual_end_date).format("DD/MM/YYYY")
                              : "---"
                          }
                          id={rental.id}
                          field="actual_end_date"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "actual_end_date",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableDateCell
                          value={rental.last_date_of_counter_activation || ""}
                          displayValue={
                            rental.last_date_of_counter_activation
                              ? dayjs(rental.last_date_of_counter_activation).format("DD/MM/YYYY")
                              : "---"
                          }
                          id={rental.id}
                          field="last_date_of_counter_activation"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "last_date_of_counter_activation",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}